import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";
import { VisibilityPreview } from "../components/VisibilityPreview";
import type { Audience, PersonView, UserSettings, Visibility } from "../api/types";

interface MeView {
  oid: string;
  display_name: string;
  email: string;
  team: string | null;
}

export function SettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const myNameFallback = accounts[0]?.name ?? t("settings.previewYouFallback");

  const { data: me } = useQuery({
    queryKey: ["me"] as const,
    queryFn: () => apiFetch<MeView>("/me"),
  });
  const [visibility, setVisibility] = useState<Visibility>("nothing");
  const [audience, setAudience] = useState<Audience>("team");
  const [showTodaySummary, setShowTodaySummary] = useState(true);
  const [favSearch, setFavSearch] = useState("");

  const { data } = useQuery({
    queryKey: ["my-settings"],
    queryFn: () => apiFetch<UserSettings>("/me/settings"),
  });

  // Picker mode: ignore audience scope so the user can favourite anyone,
  // even people outside their TEAM scope. Server still enforces target
  // visibility (NOTHING users never appear).
  const { data: people } = useQuery({
    queryKey: ["people", "picker"] as const,
    queryFn: () => apiFetch<PersonView[]>("/people?picker=true"),
  });

  const { data: favoriteOids } = useQuery({
    queryKey: ["my-favorites"],
    queryFn: () => apiFetch<string[]>("/me/favorites"),
  });

  const [favs, setFavs] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (favoriteOids) setFavs(new Set(favoriteOids));
  }, [favoriteOids]);

  useEffect(() => {
    if (data) {
      setVisibility(data.visibility);
      setAudience(data.audience);
      setShowTodaySummary(data.show_today_summary);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: { visibility: Visibility; audience: Audience; show_today_summary: boolean }) =>
      apiFetch<UserSettings>("/me/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (returned) => {
      // Prime the cache with the server's view so navigating away and back
      // doesn't show the previously-cached (stale) values.
      queryClient.setQueryData(["my-settings"], returned);
      // Other consumers (calendar shows colleagues filtered by the requester's
      // own audience too) must refetch.
      void queryClient.invalidateQueries({ queryKey: ["absences"] });
      void queryClient.invalidateQueries({ queryKey: ["people"] });
      // Return to the calendar — the user's primary view.
      navigate("/calendar");
    },
  });

  const saveFavorites = useMutation({
    mutationFn: (oids: string[]) =>
      apiFetch<string[]>("/me/favorites", {
        method: "PUT",
        body: JSON.stringify({ target_oids: oids }),
      }),
    onSuccess: (returned) => {
      queryClient.setQueryData(["my-favorites"], returned);
      // Calendar reads favourites to decide what to show.
      void queryClient.invalidateQueries({ queryKey: ["absences"] });
    },
  });

  const peopleByOid = useMemo(() => {
    const m = new Map<string, PersonView>();
    for (const p of people ?? []) m.set(p.target_oid, p);
    return m;
  }, [people]);

  const favoritesList = useMemo(
    () =>
      Array.from(favs)
        .map((oid) => peopleByOid.get(oid))
        .filter((p): p is PersonView => p !== undefined)
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [favs, peopleByOid],
  );

  const addable = useMemo(() => {
    const q = favSearch.trim().toLowerCase();
    if (!q) return [];
    return (people ?? [])
      .filter((p) => !favs.has(p.target_oid))
      .filter(
        (p) =>
          p.display_name.toLowerCase().includes(q) ||
          (p.team ?? "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [people, favs, favSearch]);

  const toggleFav = (oid: string, present: boolean) => {
    const next = new Set(favs);
    if (present) next.delete(oid);
    else next.add(oid);
    setFavs(next);
    saveFavorites.mutate(Array.from(next));
  };

  return (
    <section className="settings">
      <h2>{t("settings.title")}</h2>
      <p className="manager-notice">{t("onboarding.managerNotice")}</p>

      <fieldset>
        <legend>{t("onboarding.showLabel")}</legend>
        {(["nothing", "binary", "full"] as const).map((v) => (
          <label key={v}>
            <input
              type="radio"
              name="visibility"
              value={v}
              checked={visibility === v}
              onChange={() => setVisibility(v)}
            />
            {t(`onboarding.showOptions.${v}`)}
          </label>
        ))}
        <VisibilityPreview
          visibility={visibility}
          myOid={me?.oid}
          displayName={me?.display_name ?? myNameFallback}
        />
      </fieldset>

      <fieldset>
        <legend>{t("onboarding.audienceLabel")}</legend>
        {(["team", "company", "favorites"] as const).map((a) => (
          <label key={a}>
            <input
              type="radio"
              name="audience"
              value={a}
              checked={audience === a}
              onChange={() => setAudience(a)}
            />
            {t(`onboarding.audienceOptions.${a}`)}
          </label>
        ))}

        {audience === "favorites" && (
          <div className="fav-block">
            <h3 className="fav-block-title">{t("settings.favoritesLabel")}</h3>
            <p className="hint">{t("settings.favoritesHintActive")}</p>

            {favoritesList.length === 0 ? (
              <p className="empty-msg">{t("settings.favoritesEmpty")}</p>
            ) : (
              <ul className="fav-list">
                {favoritesList.map((p) => (
                  <li key={p.target_oid}>
                    <span>{p.display_name}</span>
                    {p.team && <span className="team-tag">· {p.team}</span>}
                    <button
                      className="ghost fav-remove"
                      onClick={() => toggleFav(p.target_oid, true)}
                      aria-label={t("settings.favoriteRemove")}
                      title={t("settings.favoriteRemove")}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="fav-add">
              <input
                type="search"
                placeholder={t("settings.favoriteSearchPlaceholder")}
                value={favSearch}
                onChange={(e) => setFavSearch(e.target.value)}
                aria-label={t("settings.favoriteSearchPlaceholder")}
              />
              {addable.length > 0 && (
                <ul className="fav-suggest">
                  {addable.map((p) => (
                    <li key={p.target_oid}>
                      <button
                        className="ghost"
                        onClick={() => {
                          toggleFav(p.target_oid, false);
                          setFavSearch("");
                        }}
                      >
                        + {p.display_name}
                        {p.team && <span className="team-tag">· {p.team}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </fieldset>

      <fieldset>
        <legend>{t("settings.displayLabel")}</legend>
        <label>
          <input
            type="checkbox"
            checked={showTodaySummary}
            onChange={(e) => setShowTodaySummary(e.target.checked)}
          />
          {t("settings.showTodaySummary")}
        </label>
      </fieldset>

      <div className="settings-actions">
        <button
          onClick={() =>
            save.mutate({
              visibility,
              audience,
              show_today_summary: showTodaySummary,
            })
          }
          disabled={save.isPending}
        >
          {t("onboarding.save")}
        </button>
      </div>
    </section>
  );
}

